import React, { useEffect, useMemo, useState } from "react";
import { TourProvider, useTour } from "@reactour/tour";
import { useTranslation } from "react-i18next";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import {
  getAdminTourSteps,
  getClientTourSteps,
  getUploadTourSteps,
} from "./components/admin/adminTourSteps";
import { getAgentTourSteps } from "./components/agent/agentTourSteps";
import Login from "components/auth/Login";
import Dashboard from "./components/admin/Dashboard";
import MobileNewBase from "./components/admin/mobile/MobileNewBase";
import DataConfirmation from "components/admin/DataConfirmation";
import NewUser from "components/admin/NewUser";
import NewClient from "components/admin/NewClient";
import "./i18n";
import LanguageSwitcher from "components/common/LanguageSwitcher";
import AssignClient from "components/admin/Assignment";
import AgentDashboard from "components/agent/AgentDashborad";
import ClientConfirm from "components/admin/ClientConfirm";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Client from "components/admin/Client";
import Achievement from "components/agent/Achievement";
import AgentWorkspace from "components/agent/AgentWorkspace";
import { ContactProvider } from "./contexts/ContactContext";
import AppInitializer from "AppInitializer";
import { VoiceProvider } from "./contexts/VoiceContext";
import ClientDashboard from "components/admin/ClientDashboard";
import ContactSearch from "components/admin/ContactSearch";
import Button from "components/common/Button";
import logoIcon from "./assets/common/logoIcon-blue.svg";
import close from "./assets/dashboard/close-icon.svg";

import CallOverlay from "components/common/CallOverlay";

// The Popup component remains the same
const TourPromptPopup = ({ onNext, onSkip }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-50">
      <div className="relative w-full max-w-sm rounded-xl bg-white p-8 text-center shadow-2xl">
        <button
          onClick={onSkip}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-800"
          aria-label="Close popup"
        >
          <img src={close} alt="close" />
        </button>
        <div className="flex justify-center">
          <div className="flex h-16 w-16">
            <img src={logoIcon} alt="logoIcon" />
          </div>
        </div>
        <h2 className="mb-5 mt-4 text-[15px] text-primary">{t("journey")}</h2>
        <div className="flex justify-center">
          <Button
            variant="primary"
            onClick={onNext}
            className="w-full md:w-[200px]"
          >
            {t("agent.noteInfo.next")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const TourController = ({ role, steps }) => {
  const [showPopup, setShowPopup] = useState(false);
  const { setIsOpen, setCurrentStep } = useTour();

  useEffect(() => {
    if (!role || steps.length === 0) return;
    const storageKey = role === "Admin" ? "adminTourShown" : "agentTourShown";
    const alreadyShown = localStorage.getItem(storageKey);
    if (!alreadyShown) {
      const timer = setTimeout(() => setShowPopup(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [role, steps]);

  const handleStartTour = () => {
    setShowPopup(false);
    setCurrentStep(0);
    setIsOpen(true);
    const storageKey = role === "Admin" ? "adminTourShown" : "agentTourShown";
    localStorage.setItem(storageKey, "true");
  };

  const handleSkipTour = () => {
    setShowPopup(false);
    const storageKey = role === "Admin" ? "adminTourShown" : "agentTourShown";
    localStorage.setItem(storageKey, "true");
  };

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/new-base"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <MobileNewBase />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <Client />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-user"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <NewUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-client"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <NewClient />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-confirmation/:clientId"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <ClientConfirm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/confirmation"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <DataConfirmation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assign-client"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <AssignClient />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-dashboard"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/contact-search"
          element={
            <ProtectedRoute allowedRoles={["Admin"]}>
              <ContactSearch />
            </ProtectedRoute>
          }
        />

        <Route
          path="/agent"
          element={
            <ContactProvider>
              <AgentDashboard />
            </ContactProvider>
          }
        />
        <Route
          path="/agent/achievement"
          element={
            <ContactProvider>
              <Achievement />
            </ContactProvider>
          }
        />

        <Route
          path="/agent/workspace"
          element={<Navigate to="/agent/workspace/cold-calls" replace />}
        />
        <Route
          path="/agent/workspace/:callType"
          element={
            <ContactProvider>
              <AgentWorkspace />
            </ContactProvider>
          }
        />

        <Route path="/" element={<Navigate to="/login" replace />} />
      </Routes>

      {showPopup && (
        <TourPromptPopup onNext={handleStartTour} onSkip={handleSkipTour} />
      )}
    </>
  );
};

const App = () => {
  const { t } = useTranslation();
  const [role, setRole] = useState(() => sessionStorage.getItem("userRole"));
  const location = useLocation();

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
  
      const normalizedError =
        reason instanceof Error
          ? reason
          : new Error(reason?.message || "Unhandled promise rejection");
  
      console.error("Global Unhandled Rejection:", normalizedError);
  
      // Prevent React dev overlay crash
      event.preventDefault();
    };
  
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
  
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    const handleStorageChange = () =>
      setRole(sessionStorage.getItem("userRole"));
    window.addEventListener("storage", handleStorageChange);

    const interval = setInterval(() => {
      const currentRole = sessionStorage.getItem("userRole");
      setRole((prev) => (prev !== currentRole ? currentRole : prev));
    }, 2000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const steps = useMemo(() => {
    if (role === "Admin" && location.pathname === "/dashboard") {
      return getAdminTourSteps(t);
    }
    if (role === "Admin" && location.pathname === "/client-dashboard") {
      return getClientTourSteps(t);
    }
    if (role === "Admin" && location.pathname === "/confirmation") {
      return getUploadTourSteps(t);
    }
    if (role === "Agent") {
      return getAgentTourSteps(t);
    }
    return [];
  }, [role, t, location.pathname]);

  return (
    <div className="min-h-screen">
      <div className="absolute right-4 top-4 z-10">
        <LanguageSwitcher />
      </div>

      <AppInitializer>
        <VoiceProvider>
          <CallOverlay />
          <TourProvider
            key={`${role}-${location.pathname}`}
            steps={steps}
            afterOpen={() => (document.body.style.overflow = "hidden")}
            beforeClose={() => (document.body.style.overflow = "auto")}
            styles={{
              popover: (base) => ({ ...base, borderRadius: 12, maxWidth: 420 }),
              maskArea: (base) => ({ ...base, rx: 8 }),
            }}
          >
            <TourController role={role} steps={steps} />
          </TourProvider>
        </VoiceProvider>
      </AppInitializer>
    </div>
  );
};

export default App;